import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Car, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { MapView } from "@/components/MapView";
import { StatusTimeline } from "@/components/StatusTimeline";
import { ChatButton, CallButton } from "@/components/TripChat";
import { WaitTimer } from "@/components/WaitTimer";
import { CLASS_LABEL } from "@/components/FareBreakdown";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, money } from "@/lib/api";
import { FoodSummary } from "@/components/FoodRequest";
import { TipPrompt } from "@/components/TipPrompt";
import { useCustomerLocation } from "@/context/CustomerLocationContext";
import { StarBadge } from "@/components/FeedbackForms";

const Searching = ({ trip, onCancel, cancelling }) => (
  <div data-testid="searching-panel" className="space-y-4">
    <div className="flex items-center gap-3">
      <span className="relative flex h-3 w-3"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-70" /><span className="relative inline-flex h-3 w-3 rounded-full bg-amber-400" /></span>
      <h2 className="text-2xl text-amber-50" data-testid="finding-driver-message">Finding your driver</h2>
    </div>
    {trip.requested_driver?.status === "unavailable" && (
      <p data-testid="requested-driver-unavailable" className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">Your requested driver isn't available — finding the nearest driver</p>
    )}
    {["pending", "offered", "exclusive"].includes(trip.requested_driver?.status) && (
      <p data-testid="requested-driver-pending" className="text-sm text-zinc-400">Requested driver: <span className="text-amber-100">{trip.requested_driver.first_name}</span></p>
    )}
    {trip.no_drivers ? (
      <p data-testid="no-drivers-message" className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">No {CLASS_LABEL[trip.vehicle_class || "standard"]} drivers available nearby — still searching</p>
    ) : (
      <p className="text-sm text-zinc-400">{trip.kind === "rental" ? "Your rental is paid and open to approved chauffeurs — the first to accept is yours." : "Your payment is confirmed. We're offering your ride to the nearest available chauffeur."}</p>
    )}
    <button data-testid="cancel-trip-button" onClick={onCancel} disabled={cancelling} className="btn-danger">
      {cancelling && <Loader2 className="h-4 w-4 animate-spin" />} Cancel request
    </button>
  </div>
);

const CancelAfterAccept = ({ trip, onCancelled }) => {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const open = async () => {
    try { setPreview((await api.get(`/trips/${trip.id}/cancel-preview`)).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/cancel`);
      setPreview(null);
      onCancelled(data);
      toast("Ride cancelled", { description: data.cancel?.fee > 0 ? `Cancel fee ${money(data.cancel.fee)} kept, ${money(data.cancel.refund)} refunded.` : "Full refund issued." });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button data-testid="cancel-accepted-trip-button" onClick={open} className="btn-danger">Cancel ride</button>
      <AlertDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <AlertDialogContent data-testid="cancel-confirm-dialog" className="border-amber-500/20 bg-[#121216]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-amber-50">Cancel this ride?</AlertDialogTitle>
            <AlertDialogDescription data-testid="cancel-fee-message" className={preview?.fee > 0 ? "text-amber-300" : "text-zinc-400"}>
              {preview?.fee > 0 ? `${preview.message}. You'll be refunded ${money(preview.refund)}.` : `No cancel fee — full refund of ${money(preview?.refund)}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-dialog-keep-button" className="rounded-full">Keep ride</AlertDialogCancel>
            <AlertDialogAction data-testid="cancel-dialog-confirm-button" disabled={busy} onClick={(e) => { e.preventDefault(); confirm(); }} className="rounded-full bg-red-600 text-white hover:bg-red-500">
              Confirm cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

const DriverCard = ({ trip, onUpdate }) => {
  const eta = trip.live_eta_min;
  const headline = { accepted: "Your driver is on the way", arrived: "Your driver has arrived", in_progress: "Enjoy the ride" }[trip.status];
  return (
    <div data-testid="driver-panel" className="space-y-4">
      <h2 className="text-2xl text-amber-50" data-testid="trip-headline">{headline}</h2>
      <div className="overflow-hidden rounded-xl bg-white/[0.03]">
        <img src="/armada-34.jpg" alt="White Nissan Armada Platinum" data-testid="driver-vehicle-image" className="h-36 w-full object-cover sm:h-44" />
        <div className="flex items-center gap-3 p-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-amber-400/40 font-serif text-lg text-amber-200">{trip.driver_first_name?.[0]}</div>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-2 font-semibold text-amber-100"><span data-testid="driver-name">{trip.driver_first_name}</span><StarBadge rating={trip.driver_rating} count={trip.driver_rating_count} testId="driver-rating" /></p>
            <p className="flex items-center gap-1.5 truncate text-sm text-zinc-400"><Car className="h-3.5 w-3.5 shrink-0" /> {trip.driver_vehicle}</p>
          </div>
          <ChatButton tripId={trip.id} myRole="customer" title={`Chat with ${trip.driver_first_name}`} />
          <CallButton tripId={trip.id} phone={trip.driver_phone} label="Call driver" />
        </div>
      </div>
      {trip.status === "arrived" && trip.timestamps?.arrived_at && <WaitTimer arrivedAt={trip.timestamps.arrived_at} />}
      {trip.status !== "arrived" && eta != null && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-500/20 p-4">
          <Clock className="h-5 w-5 text-amber-400" />
          <div>
            <p className="lux-eyebrow">ETA to {trip.live_eta_target === "destination" ? "destination" : "pickup"}</p>
            <p className="font-serif text-2xl text-amber-50" data-testid="live-eta">{Math.max(1, Math.round(eta))} min</p>
          </div>
          {trip.miles_to_pickup != null && <p className="ml-auto text-right text-xs text-zinc-500">Pickup distance at accept<br /><span className="text-zinc-300">{trip.miles_to_pickup} mi</span></p>}
        </div>
      )}
      {["accepted", "arrived"].includes(trip.status) && <CancelAfterAccept trip={trip} onCancelled={onUpdate} />}
    </div>
  );
};

const Finished = ({ trip, onDone }) => (
  <div data-testid="trip-finished-panel" className="space-y-4">
    <div className="flex items-center gap-3">
      {trip.status === "completed" ? <CheckCircle2 className="h-7 w-7 text-emerald-400" /> : <XCircle className="h-7 w-7 text-red-400" />}
      <h2 className="text-2xl text-amber-50" data-testid="trip-final-status">{trip.status === "completed" ? "Trip completed" : "Trip cancelled"}</h2>
    </div>
    {trip.status === "completed" && <p className="text-sm text-zinc-400">Total {money(trip.fare.total)} · {trip.trip_miles} mi trip{trip.wait?.amount > 0 && ` · Wait time ${money(trip.wait.amount)} (${trip.wait.status === "payment_due" ? "Payment due" : "charged"})`}</p>}
    {trip.status === "cancelled" && trip.cancel && <p data-testid="cancel-summary" className="text-sm text-zinc-400">{trip.cancel.fee > 0 ? `Cancel fee ${money(trip.cancel.fee)} · refunded ${money(trip.cancel.refund)}` : `Refunded ${money(trip.cancel.refund)}`}</p>}
    <TipPrompt trip={trip} />
    <div className="flex flex-wrap gap-3">
      <Link to={`/trips/${trip.id}`} data-testid="view-trip-detail-button" className="btn-outline-gold !py-2.5">View trip details</Link>
      <button data-testid="book-another-button" onClick={onDone} className="btn-gold !py-2.5">Book another ride</button>
    </div>
  </div>
);

export const ActiveTripPanel = ({ trip, onUpdate, onDone }) => {
  const [cancelling, setCancelling] = useState(false);
  const { status: gps, setTripActive } = useCustomerLocation();
  const live = ["searching", "accepted", "arrived", "in_progress"].includes(trip.status);
  useEffect(() => { setTripActive(live); return () => setTripActive(false); }, [live, setTripActive]);
  const sharing = !live ? "idle" : gps === "on" ? "sharing" : gps === "denied" || gps === "unavailable" ? "denied" : "idle";
  const cancel = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/cancel`);
      onUpdate(data);
      toast("Ride request cancelled", { description: data.payment.status === "refunded" ? "Your payment has been refunded." : undefined });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setCancelling(false);
    }
  };
  const finished = ["completed", "cancelled"].includes(trip.status);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,420px)_1fr] lg:gap-8" data-testid="active-trip-view">
      <div className="order-2 min-w-0 space-y-6 lg:order-1">
        <div className="lux-card p-5 sm:p-6">
          <div className="mb-6 flex items-center justify-between">
            <p className="lux-eyebrow">Current journey</p>
            <span data-testid="active-trip-status" className="text-xs text-zinc-500">{trip.status}</span>
          </div>
          {trip.status !== "cancelled" && <div className="mb-8"><StatusTimeline status={trip.status} /></div>}
          {trip.status === "searching" && <Searching trip={trip} onCancel={cancel} cancelling={cancelling} />}
          {["accepted", "arrived", "in_progress"].includes(trip.status) && <DriverCard trip={trip} onUpdate={onUpdate} />}
          {finished && <Finished trip={trip} onDone={onDone} />}
        </div>
        <div className="lux-card space-y-3 p-5 text-sm sm:p-6">
          <p className="lux-eyebrow">Route</p>
          <p className="text-zinc-300"><span className="mr-2 text-amber-400">P</span>{trip.pickup.address}</p>
          {trip.stops.map((s, i) => <p key={i} className="text-zinc-400"><span className="mr-2 text-amber-400">{i + 1}</span>{s.address}</p>)}
          <p className="border-t border-white/5 pt-3 text-zinc-400">{trip.kind === "rental" ? `Rental · ${trip.rental?.hours}h` : `${trip.trip_miles} mi · ${trip.trip_minutes} min`} · <span className="text-amber-100">{money(trip.fare.total)}</span> <span className="text-xs">(payment {trip.payment.status})</span></p>
        </div>
        <FoodSummary trip={trip} />
      </div>
      <div className="order-1 min-w-0 lg:order-2 lg:sticky lg:top-28 lg:self-start">
        <MapView className="h-[340px] sm:h-[440px] lg:h-[calc(100vh-10rem)]" pickup={trip.pickup} stops={trip.stops} route={trip.route_geometry}
          driver={["accepted", "arrived", "in_progress"].includes(trip.status) ? trip.driver_location : null} testId="active-trip-map" />
        {sharing === "sharing" && <p data-testid="customer-sharing-on" className="mt-2 text-xs text-amber-300/80">Sharing your location with your driver</p>}
        {sharing === "denied" && <p data-testid="customer-sharing-denied" className="mt-2 text-xs text-zinc-500">Location off — please turn on location so your driver can find you</p>}
      </div>
    </div>
  );
};
